import * as React from 'react';
import * as Sebrae from '@sebrae/react-core';
import {
  BaSeHeader,
  BaSeTheme,
  BaSeActionItem,
  BaSeDialogContext,
  BaSeParagraph,
  BaSeIcon,
  BaSeBreakpointContext,
} from '@base/react';
import { AppContext } from '../../components/shared/context';
import { PickComponent } from '../../components/shared/pick-company/pick-company';
import { CompanyLayout } from '../../layouts/company/company-layout';
import { isEmpresaMei } from '../../components/shared/utils/empresa/empresa-util';
import { ReactComponent as DesktopLogo } from '../../images/logo-horizontal-negativo.svg';
import { ReactComponent as MobileLogo } from '../../images/logo-vertical-negativo.svg';
import Calculadora from '../../images/user-apps/calculadoras.svg';
import EmissorNfe from '../../images/user-apps/emissor-nfe.svg';
import Planejadora from '../../images/user-apps/planejadora.svg';
import Pnbox from '../../images/user-apps/pnbox.svg';
import { role } from '../../routes';
import {
  azulFluxoCaixa,
  ContentFilledContainer,
  Flexed,
  FullWidthDiv,
  MarginDelimitedBreakpoint,
  WrapperHeader,
} from './layout-styled';
import { Footer } from './footer';
import { AccessibilityBar } from './accessibility-bar';

const LogoComponent = ({ clickCallback }) => {
  const { onDesktop, onDesktopLarge } = React.useContext(BaSeBreakpointContext);

  return (
    <FullWidthDiv>
      {(onDesktop || onDesktopLarge) && (
        <div style={{ cursor: 'pointer', width: 270 }} onClick={clickCallback}>
          <DesktopLogo />
        </div>
      )}
      <PickComponent />
    </FullWidthDiv>
  );
};

const TextLogo = ({ callback }) => (
  <div onClick={callback} style={{ width: 159 }}>
    <MobileLogo />
  </div>
);

const urlSobre =
  'https://sebrae.com.br/sites/PortalSebrae/arquivos/fluxo-de-caixa-descomplicado,96dc0972f1921910VgnVCM1000001b00320aRCRD';

export const TemplatePage = ({ isEmpresaSelected, children }) => {
  const [forceRedirect, setForceRedirect] = React.useState(null);
  const { keycloak, tokenInfo, hasRole } = Sebrae.useAuth();
  const { empresa, empresaCpe } = React.useContext(AppContext);
  const { navigateTo, isExactRoute } = Sebrae.useRouter();
  const { showNotification } = React.useContext(BaSeDialogContext);
  const cache = Sebrae.SebraeStorageUtil.inPersistentStore();
  const { inTablet, inDesktop, onDesktop, onDesktopLarge } = React.useContext(
    BaSeBreakpointContext,
  );
  const { azulSebrae36, cinzaSebrae45, cinzaSebrae97 } =
    BaSeTheme.colors.institucionais;
  const { white } = BaSeTheme.colors.defaultColors;

  const userApps = React.useMemo(
    () => ({
      closeMenuColor: azulSebrae36,
      description:
        'Conheça os serviços que o Sebrae oferece para apoiar o empreendedor brasileiro:',
      iconColor: white,
      iconName: 'apps',
      menuBackgroundColor: white,
      menuBorderColor: cinzaSebrae97,
      textColor: cinzaSebrae45,
      title: 'Serviços',
      titleColor: azulSebrae36,
      label: 'Ver todos',
      icon: 'arrow-right',
      link: 'https://sebrae.com.br/produtoseservicos',
      solutions: [
        {
          title: 'Calculadora',
          image: Calculadora,
          link: 'https://calculadora.sebrae.com.br/',
          description:
            'Equilibre as finanças e impulsione o crescimento do seu negócio',
        },
        {
          title: 'Planejadora',
          image: Planejadora,
          link: 'http://planejadora.sebrae.com.br',
          description:
            'Ferramenta para você que deseja obter crédito para seu negócio',
        },
        {
          title: 'PNBOX',
          image: Pnbox,
          link: 'https://sebrae.com.br/pnbox',
          description: 'Uma nova forma de criar Planos de Negócios',
        },
        {
          title: 'Emissor',
          image: EmissorNfe,
          link: 'https://sebrae.com.br/emissor',
          description:
            'Emita seus documentos fiscais de forma segura, ágil e gratuita',
        },
      ],
    }),
    [azulSebrae36, cinzaSebrae45, cinzaSebrae97, white],
  );

  const handleCompColor = React.useCallback(() => {
    return onDesktop || onDesktopLarge ? white : azulSebrae36;
  }, [azulSebrae36, onDesktop, onDesktopLarge, white]);

  const handleNavigation = React.useCallback(
    (route) => {
      if (cache.hasItem(tokenInfo?.sub)) {
        navigateTo(route);
      } else {
        showNotification({
          color: 'attention',
          message: 'Atenção',
          supportMessage: 'Selecione uma empresa',
        });
        localStorage.clear();
        navigateTo('/');
      }
    },
    [cache, navigateTo, showNotification, tokenInfo],
  );

  const checkHomePath = React.useCallback(
    () => isExactRoute('/'),
    [isExactRoute],
  );

  const checkSelectedCompany = React.useCallback(
    () => cache.hasItem(tokenInfo?.sub),
    [cache, tokenInfo],
  );

  const openNewTab = React.useCallback((link) => {
    return window.open(link);
  }, []);

  const showOptions = React.useCallback(() => {
    const defaultProp = [
      {
        onClick: () => navigateTo('/'),
        component: () => (
          <Flexed menu={!inTablet}>
            <BaSeIcon
              size={16}
              color={handleCompColor()}
              name="home-alt"
              description="Ir para o início"
            />
            <BaSeParagraph color={handleCompColor()}>Inicio</BaSeParagraph>
          </Flexed>
        ),
      },
    ];
    const hasEmpresaProp = [
      {
        onClick: () => handleNavigation('/lancamentos'),
        component: () => (
          <Flexed menu={!inTablet}>
            <BaSeIcon
              size={16}
              color={handleCompColor()}
              name="list-ul"
              description="Ir para lançamentos"
            />
            <BaSeParagraph color={handleCompColor()}>Lançamentos</BaSeParagraph>
          </Flexed>
        ),
      },
      {
        onClick: () => handleNavigation('/relatorio'),
        component: () => (
          <Flexed menu={!inTablet}>
            <BaSeIcon
              size={16}
              color={handleCompColor()}
              name="graph-bar"
              description="Ir para relatório"
            />
            <BaSeParagraph color={handleCompColor()}>Relatórios</BaSeParagraph>
          </Flexed>
        ),
      },
      {
        onClick: () => handleNavigation('/configuracoes'),
        component: () => (
          <Flexed menu={!inTablet}>
            <BaSeIcon
              size={16}
              color={handleCompColor()}
              name="cog"
              description="Ir para Configurações"
            />
            <BaSeParagraph color={handleCompColor()}>
              Configurações
            </BaSeParagraph>
          </Flexed>
        ),
      },
    ];

    const meiProp = [
      {
        onClick: () => handleNavigation('/boletos-mei'),
        component: () => (
          <Flexed menu={!inTablet}>
            <BaSeIcon
              size={16}
              color={handleCompColor()}
              name="file-alt"
              description="Ir para os boletos do mei"
            />
            <BaSeParagraph color={handleCompColor()}>Boletos MEI</BaSeParagraph>
          </Flexed>
        ),
      },
    ];
    const lastProp = [
      {
        component: () => (
          <BaSeActionItem
            minWidth={200}
            type="tertiary"
            size="small"
            buttonType="base-button"
            color={handleCompColor()}
            customButtonProps={{
              value: 'Mais',
              rightIcon: 'arrow-down',
              disableTextColorHover: true,
            }}
            items={[
              {
                text: 'Termo de uso',
                callback: () =>
                  openNewTab(
                    'https://minio-cpe.sebrae.com.br/documento/termo-fluxo-caixa-online.pdf',
                  ),
              },
              {
                text: 'Politica de privacidade',
                callback: () =>
                  openNewTab(
                    'https://minio-cpe.sebrae.com.br/documento/politica-privacidade.pdf',
                  ),
              },
              {
                text: 'Ajuda',
                callback: () =>
                  openNewTab('https://fluxodecaixasebrae.tawk.help/'),
              },
              {
                text: 'Historico de versões',
                callback: () => navigateTo('/historico-versoes'),
              },
              {
                text: 'Sobre',
                callback: () => openNewTab(urlSobre),
              },
            ]}
          />
        ),
      },
    ];

    const meiOpt = isEmpresaMei(empresaCpe) ? meiProp : [];
    const isHomePage = window.location.pathname === '/';

    return empresa && !isHomePage
      ? [
          hasEmpresaProp[0],
          ...meiOpt,
          ...hasEmpresaProp.splice(1, 99),
          ...lastProp,
        ]
      : [...defaultProp, ...lastProp];
  }, [
    empresa,
    empresaCpe,
    handleCompColor,
    handleNavigation,
    inTablet,
    navigateTo,
    openNewTab,
  ]);

  React.useEffect(() => {
    setForceRedirect(!checkHomePath && !checkSelectedCompany);
  }, [checkHomePath, checkSelectedCompany]);

  React.useEffect(() => {
    if (forceRedirect) {
      localStorage.clear();
      navigateTo('/');
      setForceRedirect(false);
    }
  }, [forceRedirect, navigateTo]);

  if (forceRedirect || forceRedirect === null) {
    return <React.Fragment />;
  }

  return (
    <CompanyLayout isEmpresaSelected={isEmpresaSelected}>
      <AccessibilityBar />
      <WrapperHeader>
        <BaSeHeader
          solutionsProps={userApps}
          backgroundColor={azulFluxoCaixa}
          delimitedOnBreakpoint="desktop"
          systemLogo={() =>
            !inDesktop ? (
              <TextLogo
                callback={() => {
                  localStorage.clear();
                  navigateTo('/');
                }}
              />
            ) : (
              <></>
            )
          }
          accountProfileProps={{
            keycloak,
            contextLinks: [
              hasRole(role.administrador) && {
                label: 'Gestão de changelog',
                url: '/gestao-changelog',
              },
            ].filter((ele) => !!ele),
            buttonCollapsed: true,
          }}
          customComponents={[
            {
              component: () => (
                <>
                  <LogoComponent
                    clickCallback={() => {
                      localStorage.clear();
                      navigateTo('/');
                    }}
                  />
                </>
              ),
              showOnExpanded: true,
              showOnSidebar: true,
              showOnSubmenu: true,
            },
          ]}
          menuItens={showOptions()}
        />
      </WrapperHeader>
      <ContentFilledContainer>{children}</ContentFilledContainer>
      <MarginDelimitedBreakpoint>
        <Footer />
      </MarginDelimitedBreakpoint>
    </CompanyLayout>
  );
};
